import { AgencyId, Dtos } from "@inrev/common";
import {
	useAdminCancelAgentInvitation,
	useAdminResendAgentInvitation,
} from "@portal/api/agency/admin";
import { useCancelAgentUserInvitation, useResendAgentUserInvitation } from "@portal/api/user/agent";
import { ConfirmationModalClickWrapper } from "@portal/components/layout/ConfirmationModalClickWrapper";
import { Icon } from "@portal/components/primitives";
import { Separator } from "@portal/components/ui/Separator";
import { useState } from "react";

type InvitationPendingProps = {
	agencyId?: AgencyId;
	agent: Dtos.Agent.UserPreview.Get.Response | Dtos.Agent.Preview.Get.Response;
};

export const InvitationPending = ({ agencyId, agent }: InvitationPendingProps) => {
	const [hasResent, setHasResent] = useState<boolean>(false);
	const {
		resendAgentUserInvitation,
		resendAgentUserInvitationIsLoading,
		resendAgentUserInvitationError,
	} = useResendAgentUserInvitation();
	const {
		adminResendAgentInvitation,
		adminResendAgentInvitationIsLoading,
		adminResendAgentInvitationError,
	} = useAdminResendAgentInvitation();
	const { cancelAgentUserInvitation } = useCancelAgentUserInvitation();
	const { adminCancelAgentInvitation } = useAdminCancelAgentInvitation();
	const isAdmin = agencyId !== undefined;

	const handleResend = () => {
		setHasResent(true);

		if (agencyId) {
			adminResendAgentInvitation({ agencyId, agentId: agent.id });
		} else {
			resendAgentUserInvitation(agent.id);
		}
	};

	if (!agent.invitationPending) return null;
	return (
		<div className="flex flex-col gap-[10px] w-fit shrink-0">
			<div className="text-[13px] text-secondary font-medium w-fit italic px-[12px] py-[5px] bg-surface-neutral rounded-md">
				Invitation Sent
			</div>
			<div className="flex gap-[10px] items-center justify-between text-[11px] px-[5px]">
				{!hasResent && (
					<span className="cursor-pointer hover:underline text-secondary" onClick={handleResend}>
						Resend
					</span>
				)}
				{(isAdmin ? adminResendAgentInvitationIsLoading : resendAgentUserInvitationIsLoading) && (
					<span className="text-secondary">Resending...</span>
				)}
				{hasResent &&
					((isAdmin &&
						!adminResendAgentInvitationIsLoading &&
						adminResendAgentInvitationError === null) ||
						(!isAdmin &&
							!resendAgentUserInvitationIsLoading &&
							resendAgentUserInvitationError === null)) && (
						<div className="flex items-center gap-[5px] text-brand-accent">
							<Icon type="check" size="mini" />
							<span>Resent</span>
						</div>
					)}
				{(isAdmin
					? adminResendAgentInvitationError !== null
					: resendAgentUserInvitationError !== null) && (
					<span className="text-danger">Resend Failed</span>
				)}
				<Separator orientation="vertical" className="bg-surface-neutral h-[10px]" />
				<ConfirmationModalClickWrapper
					message={`Are you sure you want to cancel this invitation for ${agent.email}?`}
					className="max-w-[500px]"
					shouldConfirm={
						(isAdmin && !adminResendAgentInvitationIsLoading) ||
						(!isAdmin && !resendAgentUserInvitationIsLoading)
					}
					onConfirm={() => {
						if (agencyId) {
							adminCancelAgentInvitation({ agencyId, agentId: agent.id });
						} else {
							cancelAgentUserInvitation(agent.id);
						}
					}}
				>
					<span
						className="cursor-pointer hover:underline text-danger"
						onClick={() => {
							if (agencyId) {
								adminCancelAgentInvitation({ agencyId, agentId: agent.id });
							} else {
								cancelAgentUserInvitation(agent.id);
							}
						}}
					>
						Cancel
					</span>
				</ConfirmationModalClickWrapper>
			</div>
		</div>
	);
};
